/* eslint-disable no-restricted-imports */
import * as React from 'react';
import { startTransition, useDeferredValue } from 'react';

import { Heading, Icon, Input, Separator, SidebarMenu, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

import { componentCatalog } from './catalog';
import { FamilyHeader, MetaPill, PublicApiSection } from './catalog/helpers';
import { collectApiSearchTerms } from './catalog/publicApi';
import componentUsageSnapshot from './catalog/usage.generated.json';

const HASH_PREFIX = '#/components/';

// Family ids whose API has been audited end-to-end (props, vocabulary,
// matrix, callsites, catalog, publicApi). Mirrors the "Done — full API
// review" section of DESIGN_SYSTEM_REVIEW.md at the project root —
// keep them in sync.
const VERIFIED_FAMILY_IDS = new Set([
    'accordion',
    'alarm-schedule',
    'alert',
    'aspect-ratio',
    'avatar',
    'backdrop',
    'badge',
    'breadcrumb',
    'button',
    'button-group',
    'container',
    'calendar',
    'card',
    'checkbox',
    'checkbox-group',
    'content-header',
    'data-grid',
    'description-list',
    'dialog',
    'dropdown-menu',
    'file-input',
    'form',
    'icon',
    'icon-button',
    'input',
    'input-otp',
    'label',
    'link',
    'list',
    'pagination',
    'password-setup',
    'popover',
    'progress-bar',
    'radio-group',
    'resizable',
    'schedule',
    'scroll-area',
    'search',
    'select',
    'separator',
    'sidebar-menu',
    'skeleton',
    'slider',
    'surface',
    'switch',
    'table',
    'tabs',
    'text-area',
    'toast',
    'tooltip',
    'transfer-list',
    'typography',
    'wizard'
]);

const VerifiedBadge = () => (
    <Icon icon="circleCheck" size="small" tone="success" />
);

const parseHashFamilyId = () => {
    const rawHash = window.location.hash;

    if (!rawHash.startsWith(HASH_PREFIX)) {
        return null;
    }

    const familyId = rawHash.slice(HASH_PREFIX.length).trim();

    return familyId || null;
};

const buildFamilyHash = familyId => `${HASH_PREFIX}${familyId}`;

const filterFamilies = (families, query) => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
        return families;
    }

    return families.filter(family => {
        const haystack = [
            family.label,
            family.id,
            family.description,
            ...family.subcomponents,
            collectApiSearchTerms(family)
        ].join(' ').toLowerCase();

        return haystack.includes(normalizedQuery);
    });
};

const exportsUsage = componentUsageSnapshot.exports ?? {};

const lookupExportUsage = name => exportsUsage[name] ?? { inApp: 0, inComponents: 0 };

const aggregateFamilyUsage = subcomponents => subcomponents.reduce(
    (acc, name) => {
        const counter = lookupExportUsage(name);

        return {
            inApp: acc.inApp + counter.inApp,
            inComponents: acc.inComponents + counter.inComponents
        };
    },
    { inApp: 0, inComponents: 0 }
);

const componentCatalogWithUsage = componentCatalog.map(family => ({
    ...family,
    verified: VERIFIED_FAMILY_IDS.has(family.id),
    usage: {
        ...aggregateFamilyUsage(family.subcomponents),
        perExport: family.subcomponents.map(name => ({
            name,
            ...lookupExportUsage(name)
        }))
    }
}));

const verifiedFamilyCount = componentCatalogWithUsage.filter(family => family.verified).length;


export default function DesignSystemComponents() {
    const [query, setQuery] = React.useState('');
    const deferredQuery = useDeferredValue(query);
    const [selectedFamilyId, setSelectedFamilyId] = React.useState(() => {
        const hashFamilyId = parseHashFamilyId();
        const matchingFamily = componentCatalogWithUsage.find(family => family.id === hashFamilyId);

        return matchingFamily?.id ?? componentCatalogWithUsage[0]?.id ?? null;
    });

    const filteredFamilies = React.useMemo(
        () => filterFamilies(componentCatalogWithUsage, deferredQuery),
        [deferredQuery]
    );

    React.useEffect(() => {
        const handleHashChange = () => {
            const hashFamilyId = parseHashFamilyId();

            if (!hashFamilyId) {
                return;
            }

            const matchingFamily = componentCatalogWithUsage.find(family => family.id === hashFamilyId);

            if (matchingFamily) {
                startTransition(() => setSelectedFamilyId(matchingFamily.id));
            }
        };

        window.addEventListener('hashchange', handleHashChange);

        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    React.useEffect(() => {
        if (!filteredFamilies.length) {
            return;
        }

        const familyVisibleInFilter = filteredFamilies.some(family => family.id === selectedFamilyId);

        if (!familyVisibleInFilter) {
            startTransition(() => setSelectedFamilyId(filteredFamilies[0].id));
        }
    }, [filteredFamilies, selectedFamilyId]);

    React.useEffect(() => {
        if (!selectedFamilyId) {
            return;
        }

        const expectedHash = buildFamilyHash(selectedFamilyId);

        if (window.location.hash !== expectedHash) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${expectedHash}`);
        }
    }, [selectedFamilyId]);

    const selectedFamily = React.useMemo(
        () => filteredFamilies.find(family => family.id === selectedFamilyId)
            ?? componentCatalogWithUsage.find(family => family.id === selectedFamilyId)
            ?? filteredFamilies[0]
            ?? componentCatalogWithUsage[0]
            ?? null,
        [filteredFamilies, selectedFamilyId]
    );

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-6 lg:tw-flex-row lg:tw-items-start lg:tw-gap-8">
            <aside className={`${WM_FLAT_SURFACE_CLASSNAME} tw-flex tw-w-full tw-shrink-0 tw-flex-col tw-gap-4 tw-rounded-3xl tw-p-5 lg:tw-w-catalog-aside lg:tw-p-6`}>
                <div className="tw-space-y-2">
                    <Text tone="muted" variant="overline">
                        WiseMetering UI
                    </Text>
                    <Heading as="h2" variant="title-lg">
                        Components
                    </Heading>
                    <Text tone="muted" variant="body">
                        Public catalog of UI primitives and business components.
                    </Text>
                </div>

                <div className="tw-bg-background">
                    <Input
                        onChange={event => setQuery(event.target.value)}
                        placeholder="Search components"
                        type="search"
                        value={query}
                    />
                </div>

                <div className="tw-flex tw-flex-wrap tw-gap-2">
                    <MetaPill>{componentCatalogWithUsage.length} families</MetaPill>
                    <MetaPill>{filteredFamilies.length} visible</MetaPill>
                    <MetaPill>Verified: {verifiedFamilyCount} of {componentCatalogWithUsage.length}</MetaPill>
                </div>

                <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-pr-1">
                    <nav className="tw-space-y-1">
                        {(() => {
                            const businessFamilies = filteredFamilies.filter(f => f.layer === 'business');
                            const uiFamilies = filteredFamilies.filter(f => f.layer !== 'business');

                            const familyToItem = family => ({
                                value: family.id,
                                label: family.label,
                                description: `${family.subcomponents.length} exports · ${family.usage.inApp} in app`,
                                trailing: family.verified ? <VerifiedBadge /> : undefined
                            });

                            const handleChange = nextValue => {
                                startTransition(() => setSelectedFamilyId(nextValue));
                            };

                            return (
                                <>
                                    {businessFamilies.length ? (
                                        <>
                                            <div className="tw-mb-1"><Text tone="muted" variant="overline">Business</Text></div>
                                            <SidebarMenu
                                                items={businessFamilies.map(familyToItem)}
                                                onChange={handleChange}
                                                tone="primary"
                                                value={selectedFamily?.id}
                                            />
                                            <div className="tw-pt-6"><Separator /></div>
                                            <div className="tw-mb-1"><Text tone="muted" variant="overline">UI</Text></div>
                                        </>
                                    ) : null}

                                    {uiFamilies.length ? (
                                        <SidebarMenu
                                            items={uiFamilies.map(familyToItem)}
                                            onChange={handleChange}
                                            tone="primary"
                                            value={selectedFamily?.id}
                                        />
                                    ) : null}

                                    {!filteredFamilies.length ? (
                                        <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-border tw-bg-muted tw-p-4 tw-text-sm tw-text-muted-foreground">
                                            No families found for &quot;{query}&quot;.
                                        </div>
                                    ) : null}
                                </>
                            );
                        })()}
                    </nav>
                </div>
            </aside>

            <main data-family-panel className={`${WM_FLAT_SURFACE_CLASSNAME} tw-min-w-0 tw-flex-1 tw-overflow-auto tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                {selectedFamily ? (
                    <div className="tw-space-y-4">
                        <FamilyHeader family={selectedFamily} />
                        <div data-preview-area className="tw-flex tw-flex-col tw-gap-8">
                            {selectedFamily.stories.map(story => (
                                <React.Fragment key={story.id}>
                                    {story.renderSection()}
                                </React.Fragment>
                            ))}
                        </div>
                        <PublicApiSection family={selectedFamily} />
                    </div>
                ) : (
                    <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-border tw-bg-muted tw-p-6 tw-text-sm tw-text-muted-foreground">
                        No component families are currently available.
                    </div>
                )}
            </main>
        </div>
    );
}
